import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { computePostOpDay } from "../lib/date";
import {
  Sparkle, Warning, EnvelopeSimple, PaperPlaneTilt, LinkSimple, Copy, FloppyDisk, Trash, Clock, Heart, Pulse, Phone, Calendar, CheckCircle,
} from "@phosphor-icons/react";

const SWELLING = [
  { v: "none", l: "Nessuno" },
  { v: "mild", l: "Lieve" },
  { v: "moderate", l: "Moderato" },
  { v: "severe", l: "Severo" },
];
const BLEEDING = [
  { v: "none", l: "Nessuno" },
  { v: "mild", l: "Lieve" },
  { v: "persistent", l: "Persistente" },
  { v: "severe", l: "Severo" },
];
const SMOKING = [
  { v: "no", l: "No" },
  { v: "occasional", l: "Occasionale" },
  { v: "yes", l: "Sì" },
];

const RISK_META = {
  normal: { label: "Decorso normale", color: "bg-[#EAFBEF] text-[#1F8B4C] border-[#1F8B4C]/30", Icon: CheckCircle },
  monitor: { label: "Da monitorare", color: "bg-[#FFFAE9] text-[#8A6D1F] border-[#8A6D1F]/30", Icon: Pulse },
  contact_clinic: { label: "Contattare lo studio", color: "bg-[#FFF3E8] text-[#B45E2E] border-[#B45E2E]/30", Icon: Phone },
  urgent: { label: "URGENTE", color: "bg-[#FAEBEB] text-[#A84A4A] border-[#A84A4A]/40", Icon: Warning },
};

const NEXT_ACTION = {
  reassure: "Rassicura il paziente",
  ask_for_photo: "Richiedi foto",
  schedule_check: "Programma controllo",
  urgent_visit: "Visita urgente",
  call_patient: "Chiama il paziente",
};

export default function FollowupAITab({ patient, settings, onAudit }) {
  const pid = patient.id;
  const surgeryDate = patient.intervention_date || "";
  const defaultDay = surgeryDate ? computePostOpDay(surgeryDate) : 1;

  const [inputs, setInputs] = useState({
    post_op_day: defaultDay,
    pain_level: 2,
    swelling: "mild",
    bleeding: "none",
    fever: false,
    temperature: "",
    bad_taste_pus: false,
    breathing_swallowing_difficulty: false,
    allergy_symptoms: false,
    current_medications: "",
    smoking: "no",
    patient_notes: "",
    photo_file_id: null,
  });

  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState("");
  const [ai, setAi] = useState(null);
  const [editedNote, setEditedNote] = useState("");
  const [editedSubject, setEditedSubject] = useState("");
  const [editedBody, setEditedBody] = useState("");
  const [history, setHistory] = useState([]);
  const [saving, setSaving] = useState(false);
  const [savedFollowupId, setSavedFollowupId] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailToast, setEmailToast] = useState(null);
  const [emailStatus, setEmailStatus] = useState(null); // 'sent' | 'failed' | 'missing_email' | null
  const [portalCheckin, setPortalCheckin] = useState(null);
  const [prefilled, setPrefilled] = useState(false);

  const loadHistory = async () => {
    try {
      const { data } = await api.get(`/patients/${pid}/followups`);
      setHistory(data);
    } catch (e) { /* soft fail */ }
  };

  const loadPortalCheckin = async () => {
    try {
      const { data } = await api.get(`/patients/${pid}/latest-portal-checkin`);
      if (data?.available) {
        setPortalCheckin(data);
        // Auto-prefill if no AI result yet and not already prefilled
        if (!prefilled && !ai) {
          const pin = data.inputs || {};
          setInputs((s) => ({
            ...s,
            post_op_day: pin.post_op_day ?? s.post_op_day,
            pain_level: pin.pain_level ?? s.pain_level,
            swelling: pin.swelling ?? s.swelling,
            bleeding: pin.bleeding ?? s.bleeding,
            fever: !!pin.fever,
            temperature: pin.temperature ?? "",
            bad_taste_pus: !!pin.bad_taste_pus,
            breathing_swallowing_difficulty: !!pin.breathing_swallowing_difficulty,
            current_medications: pin.current_medications || s.current_medications,
            smoking: pin.smoking || (pin.smoked_today ? "yes" : "no"),
            patient_notes: pin.patient_notes || s.patient_notes,
          }));
          setPrefilled(true);
        }
      }
    } catch (e) { /* soft fail */ }
  };

  useEffect(() => { loadHistory(); loadPortalCheckin(); /* eslint-disable-next-line */ }, [pid]);

  const applyPortalResponses = () => {
    if (!portalCheckin?.inputs) return;
    const pin = portalCheckin.inputs;
    setInputs((s) => ({
      ...s,
      post_op_day: pin.post_op_day ?? s.post_op_day,
      pain_level: pin.pain_level ?? s.pain_level,
      swelling: pin.swelling ?? s.swelling,
      bleeding: pin.bleeding ?? s.bleeding,
      fever: !!pin.fever,
      temperature: pin.temperature ?? "",
      bad_taste_pus: !!pin.bad_taste_pus,
      breathing_swallowing_difficulty: !!pin.breathing_swallowing_difficulty,
      current_medications: pin.current_medications || s.current_medications,
      smoking: pin.smoking || (pin.smoked_today ? "yes" : "no"),
      patient_notes: pin.patient_notes || s.patient_notes,
    }));
    setPrefilled(true);
  };

  const set = (k, v) => setInputs((s) => ({ ...s, [k]: v }));

  const generate = async () => {
    setGenerating(true);
    setError("");
    setAi(null);
    try {
      const body = { ...inputs };
      if (body.fever && body.temperature !== "") body.temperature = parseFloat(body.temperature);
      else body.temperature = null;
      const { data } = await api.post(`/patients/${pid}/followup/generate`, body);
      setAi(data);
      setEditedNote(data.clinical_note || "");
      setEditedSubject(data.email_subject || `Il tuo controllo post-operatorio — Mediadent Sarzana`);
      setEditedBody(data.email_body || "");
      setSavedFollowupId(null);
      setEmailStatus(null);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setGenerating(false);
    }
  };

  const save = async () => {
    if (!ai) return;
    setSaving(true);
    try {
      const body = { ...inputs };
      if (body.fever && body.temperature !== "" && body.temperature != null) body.temperature = parseFloat(body.temperature);
      else body.temperature = null;
      const { data: saved } = await api.post(`/patients/${pid}/followup`, {
        inputs: body,
        ai,
        edited_clinical_note: editedNote,
        edited_whatsapp_message: "",  // legacy — no more WhatsApp workflow
        edited_email_subject: editedSubject,
        edited_email_body: editedBody,
      });
      setSavedFollowupId(saved?.id || null);
      await loadHistory();
      onAudit && onAudit();
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const sendEmail = async () => {
    if (!editedSubject.trim() || !editedBody.trim()) {
      setEmailToast({ ok: false, msg: "Oggetto e corpo email obbligatori." });
      return;
    }
    setSendingEmail(true);
    setEmailToast(null);
    try {
      // Save first if not saved yet
      let fid = savedFollowupId;
      if (!fid) {
        await save();
        // save sets savedFollowupId asynchronously — read from history via a refetch
        const { data } = await api.get(`/patients/${pid}/followups`);
        fid = data?.[0]?.id || null;
        setSavedFollowupId(fid);
      }
      if (!fid) {
        setEmailToast({ ok: false, msg: "Impossibile determinare l'id follow-up. Prima salva la nota." });
        setSendingEmail(false);
        return;
      }
      const { data: res } = await api.post(`/followups/${fid}/send-manual-email`, {
        subject: editedSubject,
        body: editedBody,
      });
      if (res?.ok) {
        setEmailStatus("sent");
        setEmailToast({ ok: true, msg: `Email inviata a ${res.to_email}` });
      } else if (res?.missing_email) {
        setEmailStatus("missing_email");
        setEmailToast({ ok: false, msg: res.error || "Email paziente mancante." });
      } else {
        setEmailStatus("failed");
        setEmailToast({ ok: false, msg: res?.error || "Invio email fallito." });
      }
      await loadHistory();
    } catch (e) {
      setEmailStatus("failed");
      setEmailToast({ ok: false, msg: formatApiError(e?.response?.data?.detail) || e.message });
    } finally {
      setSendingEmail(false);
      setTimeout(() => setEmailToast(null), 5000);
    }
  };

  const copyBody = async () => {
    try { await navigator.clipboard.writeText(`${editedSubject}\n\n${editedBody}`); } catch (_) {}
  };

  const removeFollowup = async (fid) => {
    if (!window.confirm("Eliminare questo follow-up dalla timeline?")) return;
    try {
      await api.delete(`/followups/${fid}`);
      await loadHistory();
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className="space-y-6" data-testid="followup-ai-tab">
      {/* Disclaimer banner */}
      <div className="elev-card p-4 flex items-start gap-3 bg-brand-50 border-brand-200">
        <Sparkle size={20} weight="duotone" className="text-brand-700 mt-0.5 shrink-0" />
        <div className="text-sm text-[#1C2321]">
          <strong className="font-medium">AI di supporto clinico — non sostituisce il giudizio del dentista.</strong>{" "}
          <span className="text-[#56635D]">In presenza di red flag (difficoltà respiratorie/deglutizione, sanguinamento severo, febbre &gt; 38°C, gonfiore in rapido aumento, pus, dolore severo, sintomi allergici) il sistema raccomanda sempre il contatto immediato con lo studio.</span>
        </div>
      </div>

      {portalCheckin?.available && (
        <PortalResponseBanner data={portalCheckin} prefilled={prefilled} onApply={applyPortalResponses} onGenerate={() => { applyPortalResponses(); setTimeout(generate, 50); }} generating={generating} />
      )}

      {/* Inputs form */}
      <div className="elev-card p-6">
        <div className="text-xs uppercase tracking-[0.2em] text-brand-400 mb-4">Check-in post-operatorio</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field label="Giorno post-operatorio">
            <input type="number" min={0} max={365} value={inputs.post_op_day} onChange={(e) => set("post_op_day", parseInt(e.target.value || "0", 10))} data-testid="fu-post-op-day" className="input" />
          </Field>
          <Field label={`Dolore (0-10): ${inputs.pain_level}`}>
            <input type="range" min={0} max={10} value={inputs.pain_level} onChange={(e) => set("pain_level", parseInt(e.target.value, 10))} data-testid="fu-pain" className="w-full accent-brand-700" />
            <div className="flex justify-between text-[10px] text-[#56635D] mt-1 px-0.5">
              <span>0</span><span>5</span><span>10</span>
            </div>
          </Field>
          <Field label="Fumatore">
            <select value={inputs.smoking} onChange={(e) => set("smoking", e.target.value)} data-testid="fu-smoking" className="input">
              {SMOKING.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
            </select>
          </Field>

          <Field label="Gonfiore">
            <select value={inputs.swelling} onChange={(e) => set("swelling", e.target.value)} data-testid="fu-swelling" className="input">
              {SWELLING.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
            </select>
          </Field>
          <Field label="Sanguinamento">
            <select value={inputs.bleeding} onChange={(e) => set("bleeding", e.target.value)} data-testid="fu-bleeding" className="input">
              {BLEEDING.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
            </select>
          </Field>
          <Field label="Temperatura (°C, se febbre)">
            <div className="flex items-center gap-2">
              <label className="inline-flex items-center gap-2 text-sm text-[#1C2321]">
                <input type="checkbox" checked={inputs.fever} onChange={(e) => set("fever", e.target.checked)} data-testid="fu-fever" />
                Febbre
              </label>
              <input type="number" step="0.1" disabled={!inputs.fever} value={inputs.temperature} onChange={(e) => set("temperature", e.target.value)} data-testid="fu-temperature" placeholder="es. 38.2" className="input flex-1 disabled:bg-[#F0F0EE]" />
            </div>
          </Field>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
          <Checkbox label="Sapore cattivo o pus" checked={inputs.bad_taste_pus} onChange={(v) => set("bad_taste_pus", v)} testId="fu-pus" />
          <Checkbox label="Difficoltà a deglutire o respirare" checked={inputs.breathing_swallowing_difficulty} onChange={(v) => set("breathing_swallowing_difficulty", v)} testId="fu-breathing" highlight />
          <Checkbox label="Sintomi allergici (orticaria, gonfiore lingua/labbra)" checked={inputs.allergy_symptoms} onChange={(v) => set("allergy_symptoms", v)} testId="fu-allergy" highlight />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <Field label="Farmaci attualmente assunti">
            <textarea rows={2} value={inputs.current_medications} onChange={(e) => set("current_medications", e.target.value)} data-testid="fu-medications" className="input" placeholder="Es. Augmentin 1g x 2/die, Brufen 600mg al bisogno" />
          </Field>
          <Field label="Note del paziente">
            <textarea rows={2} value={inputs.patient_notes} onChange={(e) => set("patient_notes", e.target.value)} data-testid="fu-patient-notes" className="input" placeholder="Cosa riferisce il paziente?" />
          </Field>
        </div>

        <div className="mt-6 flex flex-wrap gap-3">
          <button onClick={generate} disabled={generating} data-testid="fu-generate-button" className="inline-flex items-center gap-2 bg-brand-700 hover:bg-brand-800 text-white px-5 py-3 rounded-lg disabled:opacity-60">
            <Sparkle size={18} weight="duotone" /> {generating ? "Generazione AI…" : "Genera Email di Follow-up"}
          </button>
          {ai && (
            <button onClick={save} disabled={saving} data-testid="fu-save-button" className="inline-flex items-center gap-2 bg-[#EAF0EC] text-brand-700 hover:bg-[#D5E2DB] px-5 py-3 rounded-lg disabled:opacity-60">
              <FloppyDisk size={18} weight="bold" /> {saving ? "Salvataggio…" : "Salva nota clinica"}
            </button>
          )}
        </div>
        {error && <div className="mt-3 text-sm text-[#A84A4A] bg-[#A84A4A]/10 rounded-lg px-3 py-2" data-testid="fu-error">{error}</div>}
      </div>

      {/* AI Output */}
      {ai && (
        <div className="space-y-4 fade-up" data-testid="fu-ai-output">
          <RiskBadge ai={ai} />

          <div className="elev-card p-6" data-testid="fu-email-card">
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs uppercase tracking-[0.2em] text-brand-400">Email di follow-up (italiano, tono "tu")</div>
              {emailStatus === "sent" && <span className="inline-flex items-center gap-1 text-[10px] font-medium bg-[#EAFBEF] text-[#1F8B4C] px-2 py-1 rounded-full" data-testid="fu-email-status"><CheckCircle size={12} weight="fill" /> Email inviata</span>}
              {emailStatus === "failed" && <span className="inline-flex items-center gap-1 text-[10px] font-medium bg-[#FAEBEB] text-[#A84A4A] px-2 py-1 rounded-full" data-testid="fu-email-status"><Warning size={12} weight="duotone" /> Email fallita</span>}
              {emailStatus === "missing_email" && <span className="inline-flex items-center gap-1 text-[10px] font-medium bg-[#FFF3E5] text-[#8A4A1F] px-2 py-1 rounded-full" data-testid="fu-email-status"><Warning size={12} weight="duotone" /> Email mancante</span>}
            </div>
            <label className="block text-xs uppercase tracking-[0.18em] text-[#56635D] mb-1.5">Oggetto</label>
            <input
              type="text"
              value={editedSubject}
              onChange={(e) => setEditedSubject(e.target.value)}
              maxLength={120}
              data-testid="fu-email-subject"
              className="input mb-3"
              placeholder="Il tuo controllo post-operatorio — Mediadent Sarzana"
            />
            <label className="block text-xs uppercase tracking-[0.18em] text-[#56635D] mb-1.5">Corpo email</label>
            <textarea
              value={editedBody}
              onChange={(e) => setEditedBody(e.target.value)}
              rows={Math.min(16, Math.max(8, (editedBody.match(/\n/g) || []).length + 2))}
              data-testid="fu-email-body"
              className="input font-body text-sm leading-relaxed"
              placeholder="Corpo email generato dall'AI…"
            />
            <div className="mt-3 flex flex-wrap gap-2">
              <button onClick={sendEmail} disabled={sendingEmail || !editedBody || !editedSubject} data-testid="fu-send-email" className="inline-flex items-center gap-2 bg-brand-700 hover:bg-brand-800 text-white px-4 py-2.5 rounded-lg text-sm disabled:opacity-60">
                <PaperPlaneTilt size={16} weight="duotone" /> {sendingEmail ? "Invio…" : "Invia Email"}
              </button>
              <button onClick={copyBody} data-testid="fu-copy-email" className="inline-flex items-center gap-2 bg-[#EAF0EC] text-brand-700 hover:bg-[#D5E2DB] px-4 py-2.5 rounded-lg text-sm">
                <Copy size={16} weight="duotone" /> Copia testo
              </button>
              {(patient.email || "").trim() && (
                <span className="inline-flex items-center gap-1.5 text-xs text-[#56635D] px-2 py-2">
                  <EnvelopeSimple size={14} weight="duotone" /> a <strong className="font-medium text-[#1C2321]">{patient.email}</strong>
                </span>
              )}
            </div>
            {emailToast && (
              <div className={`mt-3 text-sm rounded-lg px-3 py-2 ${emailToast.ok ? "bg-[#EAFBEF] text-[#1F8B4C] border border-[#1F8B4C]/30" : "bg-[#FAEBEB] text-[#A84A4A] border border-[#A84A4A]/30"}`} data-testid="fu-email-toast">
                {emailToast.msg}
              </div>
            )}
          </div>

          <div className="elev-card p-6">
            <div className="text-xs uppercase tracking-[0.2em] text-brand-400 mb-2">Nota clinica interna</div>
            <textarea
              value={editedNote}
              onChange={(e) => setEditedNote(e.target.value)}
              rows={4}
              data-testid="fu-clinical-note"
              className="input font-mono text-sm"
            />
          </div>

          <div className="elev-card p-6">
            <div className="text-xs uppercase tracking-[0.2em] text-brand-400 mb-2">Azione consigliata</div>
            <div className="font-heading text-lg text-[#1C2321]" data-testid="fu-next-action">
              {NEXT_ACTION[ai.next_action] || ai.next_action}
            </div>
          </div>
        </div>
      )}

      {/* Timeline */}
      <div className="elev-card p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-brand-400">Timeline follow-up</div>
            <h3 className="font-heading text-lg font-medium text-[#1C2321] mt-1">Storico AI</h3>
          </div>
          <span className="text-xs text-[#56635D]">{history.length} {history.length === 1 ? "voce" : "voci"}</span>
        </div>
        {history.length === 0 ? (
          <div className="text-sm text-[#56635D] text-center py-6">Nessun follow-up registrato per questo paziente.</div>
        ) : (
          <ul className="space-y-3" data-testid="fu-history-list">
            {history.map((h) => {
              const r = RISK_META[h.ai?.risk] || RISK_META.monitor;
              const Icon = r.Icon;
              return (
                <li key={h.id} className={`border rounded-lg p-4 ${r.color}`} data-testid={`fu-history-${h.id}`}>
                  <div className="flex items-start gap-3">
                    <Icon size={18} weight="duotone" className="mt-0.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                        <span className="font-heading font-medium">{r.label}</span>
                        <span className="text-xs">Giorno {h.inputs?.post_op_day} · {NEXT_ACTION[h.ai?.next_action] || h.ai?.next_action}</span>
                        <span className="text-xs font-mono opacity-70">{new Date(h.created_at).toLocaleString("it-IT")}</span>
                      </div>
                      <div className="text-sm mt-2 text-[#1C2321] whitespace-pre-line">{h.clinical_note}</div>
                      {(h.ai?.red_flags || []).length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          {h.ai.red_flags.map((f, i) => (
                            <span key={i} className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-white/60 border border-current">{f}</span>
                          ))}
                        </div>
                      )}
                      <div className="text-[10px] mt-2 opacity-70">Salvato da {h.saved_by_email}</div>
                    </div>
                    <button onClick={() => removeFollowup(h.id)} data-testid={`fu-delete-${h.id}`} className="opacity-60 hover:opacity-100">
                      <Trash size={14} weight="duotone" />
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <style>{`.input{width:100%;padding:.65rem .85rem;border:1px solid #E6E8E6;border-radius:.5rem;background:#fff;transition:border-color .2s} .input:focus{border-color:#2C4A3B;outline:none}`}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-[0.18em] text-[#56635D] mb-1.5">{label}</label>
      {children}
    </div>
  );
}

function Checkbox({ label, checked, onChange, testId, highlight }) {
  return (
    <label className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
      checked ? (highlight ? "bg-[#A84A4A]/10 border-[#A84A4A]/30" : "bg-brand-50 border-brand-300") : "bg-white border-[#E6E8E6] hover:border-brand-300"
    }`}>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} data-testid={testId} className="mt-0.5 accent-brand-700" />
      <span className="text-sm text-[#1C2321]">{label}</span>
    </label>
  );
}

function RiskBadge({ ai }) {
  const r = RISK_META[ai.risk] || RISK_META.monitor;
  const Icon = r.Icon;
  return (
    <div className={`p-5 rounded-xl border-2 ${r.color}`} data-testid="fu-risk-badge">
      <div className="flex items-start gap-3">
        <Icon size={28} weight="duotone" className="shrink-0 mt-0.5" />
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-[0.25em] opacity-70">Classificazione rischio</div>
          <div className="font-heading text-2xl font-medium mt-1">{r.label}</div>
          {ai.risk_reasoning && <p className="text-sm mt-2 opacity-90">{ai.risk_reasoning}</p>}
          {(ai.red_flags || []).length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {ai.red_flags.map((f, i) => (
                <span key={i} className="text-[10px] font-mono px-2 py-1 rounded-full bg-white/60 border border-current">{f}</span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function PortalResponseBanner({ data, prefilled, onApply, onGenerate, generating }) {
  const inp = data.inputs || {};
  const aiRisk = (data.ai || {}).risk || "";
  const riskKey = aiRisk === "normal" ? "green" : aiRisk === "monitor" ? "yellow" : (aiRisk === "urgent" || aiRisk === "contact_clinic") ? "red" : "";
  const riskStyle = riskKey === "red" ? "bg-[#A84A4A] text-white" : riskKey === "yellow" ? "bg-[#8A6D1F] text-white" : riskKey === "green" ? "bg-[#1F8B4C] text-white" : "bg-[#56635D] text-white";
  const riskLabel = riskKey === "red" ? "Rosso" : riskKey === "yellow" ? "Giallo" : riskKey === "green" ? "Verde" : "Da valutare";
  const date = data.submitted_at ? new Date(data.submitted_at).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "";
  return (
    <div className="elev-card p-4 border-2 border-brand-700/30 bg-brand-50" data-testid="portal-response-banner">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex-1 min-w-[260px]">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs uppercase tracking-[0.18em] text-brand-700 font-medium">Risposte del paziente</span>
            <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full uppercase ${riskStyle}`} data-testid="portal-response-risk-badge">{riskLabel}</span>
            {prefilled && <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-white border border-brand-200 text-brand-700 inline-flex items-center gap-1"><CheckCircle size={10} weight="fill" /> Modulo precompilato</span>}
          </div>
          <div className="text-sm text-[#1C2321]">Il paziente ha compilato il questionario il <strong className="font-medium">{date}</strong>.</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-3 text-xs">
            <Pill label="Dolore" value={`${inp.pain_level ?? "—"}/10`} />
            <Pill label="Gonfiore" value={inp.swelling || "—"} />
            <Pill label="Sanguinamento" value={inp.bleeding || "—"} />
            <Pill label="Febbre" value={inp.fever ? "Sì" : "No"} alert={inp.fever} />
            <Pill label="Pus" value={inp.bad_taste_pus ? "Sì" : "No"} alert={inp.bad_taste_pus} />
            <Pill label="Resp./degl." value={inp.breathing_swallowing_difficulty ? "Difficoltà" : "OK"} alert={inp.breathing_swallowing_difficulty} />
            <Pill label="Fumo oggi" value={inp.smoked_today || inp.smoking === "yes" ? "Sì" : "No"} alert={inp.smoked_today && (inp.cigarettes_today || 0) > 10} />
            <Pill label="Sigarette" value={inp.cigarettes_today ?? 0} alert={(inp.cigarettes_today || 0) > 10} />
          </div>
          {inp.patient_notes && (
            <div className="mt-3 text-sm text-[#1C2321] bg-white border border-[#E6E8E6] rounded-lg p-3" data-testid="portal-patient-notes">
              <div className="text-[10px] uppercase tracking-[0.18em] text-[#56635D] mb-1">Note dal paziente</div>
              {inp.patient_notes}
            </div>
          )}
        </div>
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        <button type="button" onClick={onApply} data-testid="apply-portal-responses" className="inline-flex items-center gap-2 bg-white border border-brand-700 text-brand-700 hover:bg-brand-50 px-3 py-2 rounded-lg text-sm">
          <CheckCircle size={14} weight="duotone" /> Apri risposte paziente (precompila modulo)
        </button>
        <button type="button" onClick={onGenerate} disabled={generating} data-testid="generate-from-portal" className="inline-flex items-center gap-2 bg-brand-700 hover:bg-brand-800 text-white px-3 py-2 rounded-lg text-sm disabled:opacity-60">
          <Sparkle size={14} weight="duotone" /> {generating ? "Generazione…" : "Genera follow-up AI da risposte paziente"}
        </button>
      </div>
    </div>
  );
}

function Pill({ label, value, alert }) {
  return (
    <div className={`rounded-md border px-2 py-1.5 ${alert ? "border-[#A84A4A]/30 bg-[#FAEBEB]" : "border-[#E6E8E6] bg-white"}`}>
      <div className="text-[9px] uppercase tracking-wider text-[#56635D]">{label}</div>
      <div className={`text-sm font-medium ${alert ? "text-[#A84A4A]" : "text-[#1C2321]"}`}>{value}</div>
    </div>
  );
}

